<template>
  <div class="d-flex">
    <div class="felix-message-container mr-3">
      <v-img
        :src="felixExpression"
        max-width="50"
        height="50"
        contain
        class="felix-message-avatar"
        :class="{ 'felix-message-bounce': showBounce }"
      ></v-img>
    </div>
    <v-card
      class="pa-3 ma-2 text-left"
      style="
        position: relative;
        box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.25);
        border-radius: 20px 20px 20px 5px;
        max-width: 75%;
      "
      color="primary"
      dark
    >
      <div class="d-flex align-center mb-2">
        <span class="caption font-weight-bold">Felix</span>

        <v-chip x-small color="success" class="ml-2">IA</v-chip>
      </div>

      <div class="message-content" v-html="formattedContent"></div>

      <!-- Context and Data Information -->
      <div v-if="showDetails && hasAdditionalInfo" class="mt-3">
        <v-expansion-panels flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="pa-0">
              <span class="caption">
                <v-icon small class="mr-1">mdi-information-outline</v-icon>
                Detalhes da resposta
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="pa-0 pt-2">
              <div v-if="message.context" class="mb-2">
                <div class="caption font-weight-bold mb-1">
                  Contexto utilizado:
                </div>
                <div class="caption context-text">{{ message.context }}</div>
              </div>

              <div v-if="message.queriedData" class="mb-2">
                <div class="caption font-weight-bold mb-1">
                  Dados consultados:
                </div>
                <div class="caption context-text">
                  {{ message.queriedData }}
                </div>
              </div>

              <div class="d-flex justify-space-between caption">
                <span v-if="message.tokensUsed"
                  >Tokens: {{ message.tokensUsed }}</span
                >
                <span v-if="message.cost"
                  >Custo: ${{ message.cost.toFixed(6) }}</span
                >
                <span v-if="message.model">Modelo: {{ message.model }}</span>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <div class="d-flex justify-between align-center mt-2">
        <div class="d-flex align-center">
          <span class="caption">
            {{ formatTime(message.createdAt) }}
          </span>
          <span v-if="message.processingTimeMs" class="caption ml-2">
            ({{ message.processingTimeMs }}ms)
          </span>
        </div>

        <div class="d-flex align-center">
          <v-btn
            v-if="hasAdditionalInfo"
            icon
            x-small
            @click="showDetails = !showDetails"
            class="ml-2"
          >
            <v-icon small>{{
              showDetails ? 'mdi-chevron-up' : 'mdi-information-outline'
            }}</v-icon>
          </v-btn>

          <v-btn icon x-small @click="copyToClipboard" class="ml-1">
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>
    <v-spacer></v-spacer>
  </div>
</template>

<script>
import moment from 'moment'

// Import Felix expressions
import FelixNeutral from '../../assets/Neutro_Corpo_1.png'
import FelixRelieved from '../../assets/Aliviado_Corpo_1.png'
import FelixScared from '../../assets/Assustado_Corpo_1.png'
import FelixDisappointed from '../../assets/Decepcionado_Corpo_1.png'

export default {
  name: 'AIAssistantMessage',

  props: {
    message: {
      type: Object,
      required: true,
      default: () => ({
        content: '',
        createdAt: '',
        role: 'Assistant',
        processingTimeMs: null,
        tokensUsed: null,
        cost: null,
        context: null,
        queriedData: null,
        model: null,
      }),
    },
  },

  data() {
    return {
      showDetails: false,
      showBounce: false,
      felixExpressions: {
        neutral: FelixNeutral,
        relieved: FelixRelieved,
        scared: FelixScared,
        disappointed: FelixDisappointed,
      },
    }
  },

  mounted() {
    // Trigger bounce animation when message appears
    this.showBounce = true
    setTimeout(() => {
      this.showBounce = false
    }, 600)
  },

  computed: {
    formattedContent() {
      if (!this.message.content) return ''

      // Enhanced formatting for AI responses
      let content = this.message.content
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold text
        .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic text
        .replace(/`(.*?)`/g, '<code>$1</code>') // Inline code
        .replace(/^### (.*$)/gim, '<h4>$1</h4>') // Headers
        .replace(/^## (.*$)/gim, '<h3>$1</h3>')
        .replace(/^# (.*$)/gim, '<h2>$1</h2>')

      // Format lists
      content = content.replace(/^- (.*$)/gim, '<li>$1</li>')
      content = content.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')

      return content
    },

    hasAdditionalInfo() {
      return (
        this.message.context ||
        this.message.queriedData ||
        this.message.tokensUsed ||
        this.message.cost ||
        this.message.model
      )
    },

    felixExpression() {
      const content = this.message.content?.toLowerCase() || ''

      if (
        content.includes('erro') ||
        content.includes('desculpe') ||
        content.includes('não consegui')
      ) {
        return this.felixExpressions.scared
      }

      if (
        content.includes('sucesso') ||
        content.includes('encontrei') ||
        content.includes('aqui está')
      ) {
        return this.felixExpressions.relieved
      }

      if (
        content.length < 50 ||
        content.includes('não encontrei') ||
        content.includes('sem resultados')
      ) {
        return this.felixExpressions.disappointed
      }

      return this.felixExpressions.neutral
    },
  },

  methods: {
    formatTime(dateTime) {
      if (!dateTime) return ''

      const now = moment()
      const messageTime = moment(dateTime)

      if (now.diff(messageTime, 'minutes') < 1) {
        return 'Agora'
      } else if (now.diff(messageTime, 'hours') < 1) {
        return `${now.diff(messageTime, 'minutes')} min atrás`
      } else if (now.diff(messageTime, 'days') < 1) {
        return messageTime.format('HH:mm')
      } else {
        return messageTime.format('DD/MM/YYYY HH:mm')
      }
    },

    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.message.content)
        this.$toast.success('Mensagem copiada!')
      } catch (error) {
        console.error('Failed to copy to clipboard:', error)
        this.$toast.error('Erro ao copiar mensagem')
      }
    },
  },
}
</script>

<style scoped>
.message-content {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: white;
  word-wrap: break-word;
}

.message-content ::v-deep strong {
  font-weight: 600;
}

.message-content ::v-deep em {
  font-style: italic;
}

.message-content ::v-deep code {
  background-color: rgba(255, 255, 255, 0.1);
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
}

.message-content ::v-deep h2,
.message-content ::v-deep h3,
.message-content ::v-deep h4 {
  margin: 12px 0 8px 0;
  font-weight: 600;
}

.message-content ::v-deep ul {
  padding-left: 20px;
  margin: 8px 0;
}

.message-content ::v-deep li {
  margin: 4px 0;
}

.context-text {
  background-color: rgba(255, 255, 255, 0.1);
  padding: 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  max-height: 100px;
  overflow-y: auto;
}

.v-expansion-panel {
  background-color: transparent !important;
}

.v-expansion-panel-header {
  min-height: 32px !important;
}

.felix-message-container {
  display: flex;
  align-items: flex-start;
  padding-top: 8px;
  padding-left: 10px;
}

.felix-message-avatar {
  transition: all 0.3s ease-in-out;
  border-radius: 50%;
}

.felix-message-bounce {
  animation: felixMessageBounce 0.6s ease-in-out;
}

@keyframes felixMessageBounce {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-5px) scale(1.1);
  }
}

.felix-message-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.2);
}
</style>
